import { Children, FC, ReactNode, isValidElement } from "react";

import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { Group } from "./Group";
import { Item } from "./Item";

import styles from "./styles.module.scss";

/** The `SidebarNav` component properties. */
export interface SidebarNavProps extends BaseProps {
  /** The component children. */
  readonly children?: ReactNode;

  /** The accessible label for the navigation region. */
  readonly ariaLabel?: string;
}

/** The `SidebarNav` sub components. */
export interface SideBarNavSubComponents {
  /** Represents a group of items in the sidebar. */
  readonly Group: typeof Group;

  /** Represents an item within the sidebar. */
  readonly Item: typeof Item;
}

/** The SideBar navigation component. */
export const SidebarNav = withSubComponents<FC<SidebarNavProps>, SideBarNavSubComponents>(
  {
    Group,
    Item,
  },
  (props) => {
    const { children, ariaLabel = "Sidebar navigation" } = props;

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [styles.SidebarNav, "sidebar-nav"],
    });

    const validElements = Children.map(children, (child) => {
      if (isValidElement(child)) return child;
      return null;
    })?.filter((child) => !!child);

    return (
      <nav {...baseHtmlProps} aria-label={ariaLabel}>
        <ul>
          {validElements?.map((element, index) => {
            let hasSeparator = index < validElements.length - 1;
            const nextItem = validElements[index + 1];

            if (element.type === Item && nextItem?.type === Item) hasSeparator = false;

            return (
              <li key={index}>
                {element}
                {hasSeparator && <div className={styles.separator} aria-hidden="true" />}
              </li>
            );
          })}
        </ul>
      </nav>
    );
  },
);
